"use client";

/**
 * SSE-streamed AI trade-off summary. Mirrors the analysis-summary
 * treatment in ResultCard (inline typing with a blinking caret
 * until `done` arrives), scaled up to a three-paragraph narrative.
 *
 * Paragraph breaks come from the LLM as literal "\n\n"; the hook
 * decodes escaped newlines, so we can rely on `whitespace-pre-wrap`
 * to render real paragraph whitespace without extra parsing.
 *
 * When traceId is null (fewer than 2 analyses succeeded, so the
 * backend declined to generate a summary), we render nothing -
 * the page shell handles the "couldn't analyze enough listings"
 * feedback via the errors list.
 */
import { useT, useLocale } from "@/lib/locale-context";
import { useCompareSummaryStream } from "@/lib/use-compare-summary-stream";
import { Wand2 } from "lucide-react";
import { Fragment, useCallback, useState, type ReactNode } from "react";
import { colorForIndex } from "./palette";

interface Props {
  traceId: string | null;
  /** Stored summary text from a replay (/compare/h/:id). When
   *  provided, we skip the SSE connection entirely and render the
   *  text statically - no token charge, no typing animation. */
  staticSummary?: string;
  /** Number of listings in the comparison - used to bound the "Elan N"
   *  / "Listing N" / "Вариант N" badge parser so an LLM that
   *  hallucinates "Listing 7" doesn't get a badge for a nonexistent
   *  slot. */
  listingCount: number;
  /** Compare-history id used for one-shot summary backfill. Older rows
   *  whose original SSE never completed have empty staticSummary and
   *  no live trace - this id lets the user generate the summary on
   *  demand by POSTing /api/compare/history/{id}/regenerate-summary.
   *  Omitted on the live POST flow where staticSummary will fill in
   *  on the next render. */
  regenerateCompareId?: number | null;
}

// Parse the LLM narrative and wrap listing references in a colored
// chip so the reader can visually tie the sentence back to the card.
// The backend prompt (compare_summary.py) tells the model to refer to
// apartments as "Elan N" / "Вариант N" / "Listing N"; we match all
// three so locale mismatches still get highlighted.
function renderWithBadges(text: string, listingCount: number): ReactNode[] {
  const pattern = /(Elan|Вариант|Listing)\s+(\d+)/g;
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(text)) !== null) {
    const n = Number.parseInt(m[2], 10);
    if (!Number.isFinite(n) || n < 1 || n > listingCount) continue;
    if (m.index > lastIndex) {
      nodes.push(<Fragment key={`t${key++}`}>{text.slice(lastIndex, m.index)}</Fragment>);
    }
    const c = colorForIndex(n - 1);
    nodes.push(
      <span
        key={`b${key++}`}
        className={`inline-flex items-baseline px-1.5 py-0.5 rounded text-[12px] font-semibold ${c.chipBg} ${c.chipText} whitespace-nowrap align-baseline`}
      >
        {m[1]} {n}
      </span>,
    );
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) {
    nodes.push(<Fragment key={`t${key++}`}>{text.slice(lastIndex)}</Fragment>);
  }
  return nodes;
}

export default function CompareSummaryPanel({
  traceId, staticSummary, listingCount, regenerateCompareId,
}: Props) {
  const t = useT();
  const { locale } = useLocale();
  const { streamingText, isStreaming, isDone, error } = useCompareSummaryStream(
    staticSummary ? null : traceId,
  );

  // Regen state: holds tokens streamed back from the on-demand
  // backfill endpoint. Kept separate from the SSE-hook state so the
  // two paths can't accidentally cross-contaminate. `regenStatus`
  // drives the panel's user-facing variants below.
  const [regenText, setRegenText] = useState("");
  const [regenStatus, setRegenStatus] = useState<
    "idle" | "streaming" | "done" | "error"
  >("idle");
  const [regenError, setRegenError] = useState<string | null>(null);

  const startRegen = useCallback(async () => {
    if (!regenerateCompareId || regenStatus === "streaming") return;
    setRegenStatus("streaming");
    setRegenText("");
    setRegenError(null);
    try {
      const res = await fetch(
        `/api/compare/history/${regenerateCompareId}/regenerate-summary`,
        {
          method: "POST",
          credentials: "include",
          headers: { "X-Locale": locale },
        },
      );
      if (!res.ok) {
        // 409 = summary was filled in by another tab between fetch
        // and click. Surface a friendly "already done, refresh" hint
        // rather than the raw HTTP code.
        const msg =
          res.status === 409
            ? t("compare.summary.regen.already_done")
            : t("compare.summary.unavailable");
        setRegenError(msg);
        setRegenStatus("error");
        return;
      }
      if (!res.body) {
        setRegenError(t("compare.summary.unavailable"));
        setRegenStatus("error");
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      // Same SSE framing as chat-api.ts streamChat: \n\n separates
      // frames, `data:` carries the payload (with `\n` escaped). We
      // only care about `token` and `done` events here.
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx: number;
        while ((idx = buffer.indexOf("\n\n")) >= 0) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          let event = "";
          let data = "";
          for (const line of frame.split("\n")) {
            if (line.startsWith("event:")) event = line.slice(6).trim();
            else if (line.startsWith("data: ")) data += line.slice(6);
            else if (line.startsWith("data:")) data += line.slice(5);
          }
          if (event === "token" && data) {
            const decoded = data.replace(/\\n/g, "\n");
            setRegenText((prev) => prev + decoded);
          } else if (event === "done") {
            setRegenStatus("done");
          }
        }
      }
      // Network closed without an explicit `done` event. If we
      // accumulated text, treat as done; otherwise surface the
      // unavailable hint.
      setRegenStatus((s) => (s === "streaming" ? "done" : s));
    } catch (e) {
      setRegenError(e instanceof Error ? e.message : "regen-failed");
      setRegenStatus("error");
    }
  }, [regenerateCompareId, regenStatus, locale, t]);

  // Card styles shared between the replay and streaming paths: white
  // with the hairline border (the quiet UI, no tinted panels).
  // globals.css swaps --color-gray-* CSS vars in dark mode, so plain
  // `bg-white` and `text-gray-800` auto-flip to the dark surface /
  // near-white text. Don't add `dark:` overrides for these - they
  // fight the swap and produce wrong colors.
  const cardClass =
    "rounded-2xl border border-gray-200 bg-white p-5 sm:p-6";
  const bodyClass =
    "text-sm sm:text-[15px] text-gray-800 leading-relaxed whitespace-pre-wrap";

  // Shared header - squircle + plain semibold title, matching the
  // card-header pattern used across the result pages (ResultCard,
  // SellerResultCard, TrendPanel). No uppercase / tracking-wide
  // eyebrow because AZ schwas render awkwardly at small caps sizes.
  const header = (
    <h2 className="mb-3 text-sm font-semibold text-gray-900">
      {t("compare.summary.title")}
    </h2>
  );

  // Replay path: use the stored text directly. Rendered with the
  // same whitespace handling as streamed summaries so paragraph
  // breaks survive.
  if (staticSummary && staticSummary.trim()) {
    return (
      <section className={cardClass}>
        {header}
        <p className={bodyClass}>
          {renderWithBadges(staticSummary, listingCount)}
        </p>
      </section>
    );
  }

  // No live trace AND no stored summary. The replay path can offer
  // a one-shot backfill via regenerateCompareId; everything else
  // (e.g. fewer than 2 analyses succeeded) renders nothing.
  if (!traceId) {
    if (!regenerateCompareId) return null;

    const regenStreaming = regenStatus === "streaming";
    const regenHasText = regenText.trim().length > 0;

    // Idle: show the empty-state hint + a single button. One-shot,
    // no token charge to the user beyond what the original /compare
    // already cost - this is a backfill of work that should have
    // landed at submit time.
    if (regenStatus === "idle") {
      return (
        <section className={cardClass}>
          {header}
          <p className="text-sm text-gray-700 leading-relaxed mb-4">
            {t("compare.summary.regen.empty_hint")}
          </p>
          <button
            type="button"
            onClick={startRegen}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl font-semibold text-sm hover:bg-indigo-700 transition-colors"
          >
            <Wand2 className="w-4 h-4" aria-hidden />
            {t("compare.summary.regen.button")}
          </button>
        </section>
      );
    }

    // Streaming / done: same body treatment as the live SSE path.
    return (
      <section className={cardClass}>
        {header}

        {!regenHasText && regenStreaming && (
          <div className="space-y-2" aria-live="polite">
            <div className="h-3 bg-gray-100 rounded animate-pulse w-[90%]" />
            <div className="h-3 bg-gray-100 rounded animate-pulse w-[70%]" />
            <div className="h-3 bg-gray-100 rounded animate-pulse w-[80%]" />
            <p className="text-xs text-gray-500 pt-2">
              {t("compare.summary.skeleton")}
            </p>
          </div>
        )}

        {regenHasText && (
          <p className={bodyClass}>
            {renderWithBadges(regenText, listingCount)}
            {regenStreaming && (
              <span
                aria-hidden
                className="ml-0.5 inline-block w-[1ch] animate-pulse"
              >
                ▌
              </span>
            )}
          </p>
        )}

        {regenStatus === "error" && (
          <p className="text-xs text-gray-500">
            {regenError || t("compare.summary.unavailable")}
          </p>
        )}
      </section>
    );
  }

  const hasText = streamingText.trim().length > 0;

  return (
    <section className={cardClass}>
      {header}

      {!hasText && isStreaming && (
        <div className="space-y-2" aria-live="polite">
          <div className="h-3 bg-gray-100 rounded animate-pulse w-[90%]" />
          <div className="h-3 bg-gray-100 rounded animate-pulse w-[70%]" />
          <div className="h-3 bg-gray-100 rounded animate-pulse w-[80%]" />
          <p className="text-xs text-gray-500 pt-2">
            {t("compare.summary.skeleton")}
          </p>
        </div>
      )}

      {hasText && (
        <p className={bodyClass}>
          {renderWithBadges(streamingText, listingCount)}
          {isStreaming && (
            // Blinking caret - CSS `animate-pulse` is close enough and
            // already available from Tailwind; keeps us off custom
            // keyframes. Hidden once the stream is done so the final
            // rendered paragraph doesn't flicker forever.
            <span
              aria-hidden
              className="ml-0.5 inline-block w-[1ch] animate-pulse"
            >
              ▌
            </span>
          )}
        </p>
      )}

      {!hasText && error && isDone && (
        <p className="text-xs text-gray-500">{t("compare.summary.unavailable")}</p>
      )}
    </section>
  );
}
